/**
 * Trusted app-authored templates. Record values are passed as Alpine scope,
 * never interpolated into HTML. This is shared by all repeating surfaces.
 */
export class ComponentTemplates {
  constructor(host) {
    this.host = host;
    this.mounted = [];
    this.onAlpine = () => host.scheduleRender();
    this.observer = new MutationObserver(changes => {
      if (changes.some(change => [...change.addedNodes, ...change.removedNodes]
        .some(node => node.nodeType === 1 && node.matches('template[data-g-slot]')))) {
        host.scheduleRender();
      }
    });
  }

  connect() {
    document.addEventListener('alpine:initialized', this.onAlpine);
    this.observer.observe(this.host, { childList: true });
  }

  disconnect() {
    document.removeEventListener('alpine:initialized', this.onAlpine);
    this.observer.disconnect();
    this.dispose();
  }

  begin() {
    this.templates = new Map();
    this.pending = [];
    for (const child of this.host.children) {
      if (child.matches('template[data-g-slot]')) {
        this.templates.set(child.getAttribute('data-g-slot'), child);
      }
    }
  }

  has(name) { return this.templates.has(name); }

  region(name, record = null, index = -1, column = null) {
    const template = this.templates.get(name);
    if (!template) return '';
    const id = this.pending.length;
    this.pending.push({ template, record, index, column });
    // Inert until the host Alpine scope is ready (including late script load).
    return `<div data-g-extension-mount="${id}" hidden x-ignore></div>`;
  }

  dispose() {
    for (const { node, alpine, undoScope } of this.mounted) {
      alpine.destroyTree(node);
      undoScope();
    }
    this.mounted = [];
  }

  commit(html) {
    const alpine = globalThis.Alpine;
    const owner = this.host.closest('[x-data]');
    const ready = alpine && (!owner || owner._x_dataStack);
    const render = () => {
      this.dispose();
      this.host._root.innerHTML = html;
      if (!ready) return;
      for (const node of this.host._root.querySelectorAll('[data-g-extension-mount]')) {
        const { template, record, index, column } = this.pending[Number(node.dataset.gExtensionMount)];
        const scope = alpine.reactive({
          $record: record, $index: index, $column: column,
          $value: column && record ? record[column.field] : undefined,
        });
        node.removeAttribute('x-ignore');
        const undoScope = alpine.addScopeToNode(node, scope, this.host);
        node.appendChild(template.content.cloneNode(true));
        this.mounted.push({ node, alpine, undoScope });
        alpine.initTree(node);
        node.hidden = false;
      }
    };
    // Own insertion AND Alpine initialization: the observer must not initialize
    // the same effects/listeners again or clean up a scope still in use.
    if (alpine) alpine.mutateDom(render);
    else render();
  }
}
